// A page's live room (docs/live-design.md §3.1–3.2, §3.9): who's editing it, how many are reading it, and, for a reader,
// the page fetched again when someone saves it (through the normal page route, so nothing hidden ever arrives). In the
// editor it says when someone else saved meanwhile. The connection is opened only where this element is.

type Person = { id: string; name: string };
type Presence = { pageId: string; editing: Person[]; reading: number };
type Hub = import("@microsoft/signalr").HubConnection;

let hub: Promise<Hub> | null = null;
const rooms = new Set<P9LivePage>();

/** The one connection, opened when the first room needs it; it reconnects by itself and rejoins every room. */
export function connection(): Promise<Hub> {
  hub ??= (async () => {
    const { HubConnectionBuilder, LogLevel } = await import("@microsoft/signalr");
    const h = new HubConnectionBuilder()
      .withUrl(`${document.body.dataset.base ?? "/"}live`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
    h.on("presence", (p: Presence) => rooms.forEach((r) => r.presence(p)));
    h.on("changed", (c: { pageId: string; revisionId: string }) => rooms.forEach((r) => r.changed(c.pageId, c.revisionId)));
    h.on("closed", (c: { pageId: string; reason: string }) => rooms.forEach((r) => r.closed(c.pageId, c.reason)));
    h.on("readonly", (c: { pageId: string; reason: string }) => rooms.forEach((r) => r.readonly(c.pageId, c.reason)));
    h.onreconnected(() => rooms.forEach((r) => void r.join()));
    await h.start();
    return h;
  })();
  return hub;
}

export class P9LivePage extends HTMLElement {
  #shown = "";

  get #page(): string {
    return this.dataset.page ?? "";
  }

  connectedCallback(): void {
    if (!/^[0-9a-f-]{36}$/i.test(this.#page)) return;
    this.#shown = this.dataset.revision ?? "";
    rooms.add(this);
    void this.join();
  }

  disconnectedCallback(): void {
    rooms.delete(this);
    void hub?.then((h) => h.invoke("Leave", this.#page)).catch(() => {});
  }

  async join(): Promise<void> {
    try {
      const h = await connection();
      const result = (await h.invoke("Join", this.#page, this.dataset.editing === "true")) as { editing: boolean; presence: Presence };
      this.presence(result.presence);
    } catch {
      // Without the live connection the page works as before.
      this.replaceChildren();
    }
  }

  /** "Anna and Ben are editing · 3 reading" (yourself left out). */
  presence(p: Presence): void {
    if (p.pageId !== this.#page) return;
    const others = p.editing.filter((e) => e.id !== this.dataset.me);
    const readers = Math.max(0, p.reading - (this.dataset.editing === "true" || others.length < p.editing.length ? 0 : 1));
    const parts: string[] = [];
    if (others.length) parts.push(`${names(others.map((o) => o.name))} ${others.length === 1 ? "is" : "are"} editing`);
    if (readers) parts.push(`${readers} reading`);
    this.textContent = parts.join(" · ");
    this.title = parts.length ? "Who has this page open right now" : "";
    this.hidden = parts.length === 0;
  }

  /** Someone saved the page: a reader gets it fresh; an editor is told. */
  async changed(pageId: string, revisionId: string): Promise<void> {
    if (pageId !== this.#page || revisionId === this.#shown) return;
    this.#shown = revisionId;
    if (this.dataset.editing === "true") {
      notice(this, "Someone else saved this page just now. When you save, you'll see what they changed and can work it in.");
      return;
    }
    try {
      const response = await fetch(location.href, { headers: { Accept: "text/html" } });
      if (!response.ok) return;
      const fresh = new DOMParser().parseFromString(await response.text(), "text/html");
      for (const selector of [".page-body article.prose", ".page-head h1", ".page-head .page-tags"]) {
        const now = document.querySelector(selector);
        const next = fresh.querySelector(selector);
        if (!now || !next) continue;
        const imported = document.importNode(next, true);
        now.replaceWith(imported);
        // Boards, maps, sheets and Tapestry boxes in it are placeholders again: fill them in.
        document.dispatchEvent(new CustomEvent("p9-page-refreshed", { detail: imported }));
      }
      notice(this, "Updated just now.", 4000);
    } catch {
      // The page stays as it was; a reload shows the new version.
    }
  }

  closed(pageId: string, reason: string): void {
    if (pageId !== this.#page) return;
    rooms.delete(this);
    notice(this, `${reason} Reload to see what you may.`);
  }

  readonly(pageId: string, reason: string): void {
    if (pageId !== this.#page) return;
    this.dataset.editing = "false";
    notice(this, `${reason} Copy what you wrote before leaving.`);
  }
}

function names(list: string[]): string {
  return list.length <= 1 ? (list[0] ?? "") : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

// A short line next to the presence, gone after a while (or until the next one).
function notice(room: HTMLElement, text: string, ms?: number): void {
  let line = room.nextElementSibling as HTMLElement | null;
  if (!line?.classList.contains("live-notice")) {
    line = document.createElement("p");
    line.className = "live-notice notice small";
    room.after(line);
  }
  line.textContent = text;
  if (ms) window.setTimeout(() => line!.remove(), ms);
}

customElements.define("p9-live-page", P9LivePage);
